/* eslint-disable react-refresh/only-export-components */
import { message } from 'antd';
// The shared instance that `src/i18n.ts` initialises; imported directly so this context does
// not pull in that module's load-time side effects (it reads localStorage on import).
import i18next from 'i18next';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { isPreviewFrame } from '../domain/categoryPermissions';
import type { LoginIdentity } from '../domain/loginUsers';
import {
    DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES,
    resolveSessionIdleTimeoutMinutes,
} from '../domain/sessionActivity';
import { useSessionActivity } from '../hooks/useSessionActivity';

// API base URL (robust resolution for dev/docker)
const API_BASE_URL = (() => {
    const envUrl: string | undefined = import.meta.env.VITE_APP_API_URL;
    if (envUrl && envUrl.trim().length > 0) return envUrl;
    // If running in browser (Docker/nginx serves frontend and proxies /api)
    if (typeof window !== 'undefined') return '/api';
    // Fallback for local dev backend
    return 'http://localhost:5000/api';
})();

interface User {
    id: string;
    username: string;
    email?: string;
    role?: string;
    lastLogin?: string;
    /**
     * The pages the user's category lets them open. `null` for an administrator, who is not
     * restricted. Absent only from a server that predates user categories.
     */
    allowedPages?: string[] | null;
}

interface AuthResponse {
    success: boolean;
    error?: string;
    token?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    /**
     * Sign in, replacing any current session only once the credentials are accepted.
     * `successMessage` overrides the default toast, e.g. when switching users in place.
     */
    login: (
        identity: LoginIdentity,
        password: string,
        successMessage?: string,
    ) => Promise<AuthResponse>;
    logout: () => Promise<void>;
    changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResponse>;
    refreshToken: () => Promise<AuthResponse>;
    /** Minutes without mouse or keyboard activity after which the user is logged out. */
    sessionIdleTimeoutMinutes: number;
    /** Apply a timeout saved on the Configuration page without waiting for the next heartbeat. */
    applySessionIdleTimeoutMinutes: (minutes: unknown) => void;
    API_BASE_URL: string;
}

/** Shared by the logout toasts so a later login can close the persistent idle-logout warning. */
const LOGOUT_MESSAGE_KEY = 'logout-msg';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [sessionIdleTimeoutMinutes, setSessionIdleTimeoutMinutes] = useState(
        DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES,
    );
    const logoutInProgressRef = useRef(false);

    const applySessionIdleTimeoutMinutes = useCallback((minutes: unknown) => {
        setSessionIdleTimeoutMinutes(resolveSessionIdleTimeoutMinutes(minutes));
    }, []);

    const login = useCallback(
        async (
            identity: LoginIdentity,
            password: string,
            successMessage?: string,
        ): Promise<AuthResponse> => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/auth/login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ ...identity, password }),
                });

                const data = await response.json();

                if (response.ok) {
                    setToken(data.token);
                    setUser(data.user);
                    applySessionIdleTimeoutMinutes(data.sessionIdleTimeoutMinutes);
                    localStorage.setItem('token', data.token);
                    // The idle-logout warning never closes on its own; it is stale once back in.
                    message.destroy(LOGOUT_MESSAGE_KEY);
                    message.success(successMessage ?? 'Login successful');
                    return { success: true };
                } else {
                    message.error(data.message ?? 'Login failed');
                    return { success: false, error: data.message };
                }
            } catch (error) {
                console.error('Login error:', error);
                message.error('Network error. Please try again.');
                return { success: false, error: 'Network error' };
            } finally {
                setLoading(false);
            }
        },
        [applySessionIdleTimeoutMinutes],
    );

    const endSession = useCallback(
        async (reason: 'manual' | 'idle') => {
            if (logoutInProgressRef.current) return;
            logoutInProgressRef.current = true;
            try {
                if (token) {
                    await fetch(`${API_BASE_URL}/auth/logout`, {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                }
            } catch (error) {
                console.error('Logout error:', error);
            } finally {
                setUser(null);
                setToken(null);
                localStorage.removeItem('token');
                if (reason === 'idle') {
                    // Stays until dismissed: the user was away and would miss a brief toast.
                    message.warning({
                        content: i18next.t('login.idleLogout', {
                            minutes: sessionIdleTimeoutMinutes,
                        }),
                        key: LOGOUT_MESSAGE_KEY,
                        duration: 0,
                    });
                } else {
                    message.success({
                        content: 'Logged out successfully',
                        key: LOGOUT_MESSAGE_KEY,
                    });
                }
                logoutInProgressRef.current = false;
            }
        },
        [token, sessionIdleTimeoutMinutes],
    );

    const logout = useCallback(() => endSession('manual'), [endSession]);

    const logoutAfterIdle = useCallback(() => {
        void endSession('idle');
    }, [endSession]);

    const validateToken = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
            } else if (response.status === 401 || response.status === 403) {
                // Token is invalid
                void logout();
            } else {
                // For 5xx errors, don't logout, just assume still logged in but backend is struggling
                console.warn(`Token validation returned ${response.status}, keeping session`);
            }
        } catch (error) {
            console.error('Token validation error:', error);
            // Don't logout on network errors (could be transient 502s)
        } finally {
            setLoading(false);
        }
    }, [logout, token]);

    useEffect(() => {
        // Check if user is already logged in
        if (token) {
            void validateToken();
        } else {
            setLoading(false);
        }
    }, [token, validateToken]);

    const changePassword = useCallback(
        async (currentPassword: string, newPassword: string): Promise<AuthResponse> => {
            try {
                const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ currentPassword, newPassword }),
                });

                const data = await response.json();

                if (response.ok) {
                    message.success('Password changed successfully');
                    return { success: true };
                } else {
                    message.error(data.message ?? 'Failed to change password');
                    return { success: false, error: data.message };
                }
            } catch (error) {
                console.error('Change password error:', error);
                message.error('Network error. Please try again.');
                return { success: false, error: 'Network error' };
            }
        },
        [token],
    );

    const refreshToken = useCallback(async (): Promise<AuthResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (response.ok) {
                setToken(data.token);
                localStorage.setItem('token', data.token);
                return { success: true, token: data.token };
            } else {
                void logout();
                return { success: false };
            }
        } catch (error) {
            console.error('Refresh token error:', error);
            void logout();
            return { success: false };
        }
    }, [token, logout]);

    // Keeps the server-side session open while the user works. Only an explicit refusal ends
    // the session here; a network error or a 5xx is left for the next heartbeat to retry.
    const recordSessionActivity = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/activity`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                applySessionIdleTimeoutMinutes(data.sessionIdleTimeoutMinutes);
            } else if (response.status === 401 || response.status === 403) {
                void logout();
            }
        } catch (error) {
            console.warn('Session activity heartbeat failed, will retry:', error);
        }
    }, [token, logout, applySessionIdleTimeoutMinutes]);

    useSessionActivity({
        // Not in the permissions preview: that frame never sees the mouse or keyboard, so its idle
        // timer would run out and sign the administrator out of the real session.
        enabled: token !== null && !isPreviewFrame(window),
        idleTimeoutMinutes: sessionIdleTimeoutMinutes,
        onIdle: logoutAfterIdle,
        onHeartbeat: recordSessionActivity,
    });

    const value = useMemo(
        () => ({
            user,
            token,
            loading,
            login,
            logout,
            changePassword,
            refreshToken,
            sessionIdleTimeoutMinutes,
            applySessionIdleTimeoutMinutes,
            API_BASE_URL,
        }),
        [
            user,
            token,
            loading,
            login,
            logout,
            changePassword,
            refreshToken,
            sessionIdleTimeoutMinutes,
            applySessionIdleTimeoutMinutes,
        ],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
